import React from 'react'
import { useHistory } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Chip, IconButton, useTheme } from '@mui/material'
import { CloseCircle, Notification, TickCircle } from 'iconsax-react'
import { track } from '@SonarCX/lib/mixpanel'

import NotificationMenu from './NotificationMenu'
import MobileMenu from './NotificationMenu/MobileMenu'
import NewCount from '../BaseConversation/NewCount'
import Tooltip from '../../atoms/Tooltip'

import { Routes } from '@SonarCX/Routes'
import { replaceMentions, toTitleCase } from '@SonarCX/utilities'
import { filterNotifications } from '@SonarCX/pages/Notifications/helpers'
import { selectDMs } from '@SonarCX/slices/dms'
import {
  selectNotification,
  markRead as markNotificationRead,
  markAllRead
} from '@SonarCX/slices/notification'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import {
  useSocket,
  CONVERSATION_FILTERS
} from '@SonarCX/providers/SocketProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useStyles from './styles'

const LIMIT = 6

const NotificationsMenu = ({
  targetRef,
  setOpen = isOpen => {},
  fullHeigth = false,
  useSmallDot = false
}) => {
  const history = useHistory()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { dms } = useAppSelector(selectDMs)
  const { unread } = useAppSelector(selectNotification)

  const { getUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const {
    api: { handleFilterChange }
  } = useSocket()
  const { isExtraSmall } = useBreakpoint()

  const notifications = React.useMemo(() => Object.values(unread), [unread])
  const [type, setType] = React.useState('unread')
  const agents = React.useMemo(
    () => [...Object.values(getUsers(false)), currentUser()].filter(Boolean),
    [getUsers, currentUser]
  )

  const classes = useStyles()

  const options = React.useMemo(() => {
    const filtered = filterNotifications({
      notifications,
      type
    })

    if (filtered.length === 0) {
      return [null]
    }

    return filtered.map(n => ({
      ...n,
      key: n.id,
      body: replaceMentions(n.body, agents)
    }))
  }, [notifications, type, agents])

  const handleSelect = React.useCallback(
    item => {
      if (notifications.length === 0 || !item) {
        return
      }

      const { id, source_id, source_session_id, type, source } = item
      dispatch(markNotificationRead(id))
      track('NOTIFICATION_MARK_READ', { id })

      if (source === 'task') {
        history.push(`${Routes.TASKS}/${source_id}`)
        return
      }

      if (type !== 'mention') {
        console.warn(`Unknown notification: `, { item })
        return
      }

      if (!source_session_id) {
        console.warn(`Missing source session id`, { item })
      }

      // Group chat clicked
      if (dms[source_session_id]) {
        history.push(`${Routes.PEER_CHAT}?session=${source_session_id}`)
        return
      }

      // Regular conversation
      handleFilterChange(CONVERSATION_FILTERS.OPEN, false)
      history.push(
        `${Routes.CLIENT_CHATS}?session=${source_session_id}&id=${source_id}`
      )
    },
    [notifications.length, dispatch, dms, handleFilterChange, history]
  )

  const handleMarkAllRead = React.useCallback(() => {
    track('NOTIFICATION_MARK_ALL_READ')
    dispatch(markAllRead())
  }, [dispatch])

  const MenuComponent = React.useMemo(
    () => (isExtraSmall ? MobileMenu : NotificationMenu),
    [isExtraSmall]
  )

  return (
    <MenuComponent
      targetRef={targetRef}
      options={options}
      onSelect={handleSelect}
      LIMIT={LIMIT}
      fullHeigth={fullHeigth}
      extraTopChildren={close => (
        <div className={classes.titleArea}>
          <div className='row'>
            <div className='title'>Notifications</div>

            <IconButton onClick={close} className='closeIcon'>
              <CloseCircle size={theme.size.xl} />
            </IconButton>
          </div>

          <div className='row'>
            <div className='tabs'>
              {['unread', 'mentions'].map((title, i) => (
                <Chip
                  key={`${title}-${i}`}
                  label={toTitleCase(title)}
                  onClick={() => setType(title)}
                  color={title === type ? 'primary' : 'secondary'}
                />
              ))}
            </div>

            <IconButton onClick={handleMarkAllRead}>
              <Tooltip arrow title='Clear All'>
                <TickCircle
                  size={theme.size.xl}
                  variant='Bold'
                  color={theme.palette.primary.main}
                />
              </Tooltip>
            </IconButton>
          </div>

          <div className='separator' />
        </div>
      )}
    >
      <div className={classes.root}>
        <Tooltip title='Notifications' arrow>
          <IconButton
            onClick={() => setOpen(false)}
            className={classes.iconBtn}
          >
            <Notification size={theme.size.lg} color='black' />
          </IconButton>
        </Tooltip>

        {notifications.length > 0 && (
          <NewCount
            count={notifications.length}
            className={useSmallDot ? classes.dot : classes.count}
          />
        )}
      </div>
    </MenuComponent>
  )
}

export default NotificationsMenu
